import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Scale } from 'lucide-react';
import {
  UniversalPage,
  UniversalTable,
  type UniversalTableColumn,
  Button,
  Card,
  Modal,
  ModalBody,
  ModalFooter,
  FormField,
  Textarea,
  NotificationCard,
  StatusBadge,
  useToast,
} from '@afrilink/ui';
import {
  adminDomainService,
  type RecoupmentCase,
} from '@/domains/domains-registry/services/adminDomainService';
import { formatDate, formatZAR } from '@/shared/utils/format';

/**
 * Billing → Recoupment — the open Terms 6.4(c) cases.
 *
 * 2026-09-25 (domain gap analysis G9). `v_admin_domain_recoupment_due` and
 * the `domain-recoupment-invoice` EF shipped on 2026-09-11 with no screen:
 * the F4b trap — a correct view nobody looks at is not a control. Dikete's
 * invoice was issued by SQL because of it.
 *
 * Two outcomes only, `Invoice` or `Waive` (reason mandatory). There is
 * deliberately no "do nothing" — that is what happened to diketeevents.co.za.
 * §6.4(d) waives the amount where SightHost is at fault, and no query can
 * know that; the decision is a human's and goes on the record either way.
 */
export default function AdminRecoupmentPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [rows, setRows] = useState<RecoupmentCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const [selected, setSelected] = useState<RecoupmentCase | null>(null);
  const [mode, setMode] = useState<'invoice' | 'waive'>('invoice');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await adminDomainService.listRecoupmentDue());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  function open(c: RecoupmentCase, m: 'invoice' | 'waive') {
    setSelected(c);
    setMode(m);
    setReason('');
    setSubmitError(null);
  }

  async function submit() {
    if (!selected) return;
    if (mode === 'waive' && !reason.trim()) {
      setSubmitError('A waiver must say why no amount is payable (Terms 6.4(d)).');
      return;
    }
    setBusy(true);
    setSubmitError(null);
    try {
      const r = await adminDomainService.decideRecoupment(selected.domain_name, mode, reason.trim() || undefined);
      if (r.action === 'invoiced') {
        toast({
          tone: 'success',
          title: `Invoice ${r.invoice_number} issued`,
          body: `${formatZAR(r.amount)} for ${r.domain_name}${r.emailed ? ' — emailed to the customer' : ' — NOT emailed (no address on file)'}`,
        });
      } else {
        toast({ tone: 'success', title: 'Recoupment waived', body: `${r.domain_name} — ${formatZAR(r.amount_waived)} waived under 6.4(d).` });
      }
      setSelected(null);
      await reload();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const columns = useMemo<UniversalTableColumn<RecoupmentCase>[]>(
    () => [
      {
        key: 'domain',
        header: 'Domain',
        sortValue: (r) => r.domain_name,
        render: (r) => <span className="font-medium text-foreground">{r.domain_name}</span>,
      },
      {
        key: 'customer',
        header: 'Customer',
        sortValue: (r) => r.customer_name ?? r.customer_email ?? '',
        render: (r) => (
          <div className="flex flex-col">
            <span className="text-foreground">{r.customer_name ?? '—'}</span>
            <span className="text-xs text-muted-foreground">{r.customer_email ?? ''}</span>
          </div>
        ),
      },
      {
        key: 'term',
        header: 'Hosting term',
        sortValue: (r) => r.hosting_ended_on,
        render: (r) => (
          <span className="text-sm text-foreground">
            {formatDate(r.hosting_activated_on)} → {formatDate(r.hosting_ended_on)}
          </span>
        ),
      },
      {
        key: 'short',
        header: 'Short of 12 months',
        align: 'right',
        sortValue: (r) => r.days_short_of_minimum_term,
        render: (r) => <span className="tabular-nums text-foreground">{r.days_short_of_minimum_term} days</span>,
      },
      {
        key: 'amount',
        header: 'Amount',
        align: 'right',
        sortValue: (r) => r.amount_due,
        render: (r) => (
          <div className="flex items-center justify-end gap-2">
            <span className="font-semibold tabular-nums text-foreground">{formatZAR(r.amount_due)}</span>
            {r.price_needs_checking && <StatusBadge tone="expiring">check price</StatusBadge>}
          </div>
        ),
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        render: (r) => (
          <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
            <Button size="sm" variant="outline" onClick={() => open(r, 'waive')}>Waive</Button>
            <Button size="sm" variant="primary" onClick={() => open(r, 'invoice')}>Invoice</Button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <UniversalPage
      title="Domain recoupment"
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Billing', href: '/admin/billing' },
        { label: 'Recoupment' },
      ]}
    >
      <Card interactive={false} className="border-info/30 bg-info/5">
        <div className="p-4 text-sm text-foreground">
          <p>
            A free bundled domain whose hosting ended inside the 12-month Minimum Term. Terms 6.4(c)
            lets SightHost recover the first-year registration fee; 6.4(d) waives it where SightHost
            was at fault. Every case here needs one of the two decisions — leaving it is how
            diketeevents.co.za left with an R89 domain unpaid.
          </p>
        </div>
      </Card>

      <UniversalTable<RecoupmentCase>
        data={rows}
        columns={columns}
        getRowKey={(r) => r.domain_name}
        onRowClick={(r) => navigate(`/admin/customers/${r.customer_id}/billing`)}
        isLoading={loading}
        error={error}
        caption={loading ? undefined : `${rows.length} open case${rows.length === 1 ? '' : 's'}`}
        empty={{
          icon: Scale,
          title: 'No open recoupment cases',
          description: 'Every bundled domain whose hosting ended early has been invoiced or waived.',
        }}
      />

      <Modal
        open={selected !== null}
        onOpenChange={(o) => { if (!o) setSelected(null); }}
        title={mode === 'invoice' ? 'Issue recoupment invoice' : 'Waive recoupment'}
        description={selected ? `${selected.domain_name} · ${formatZAR(selected.amount_due)}` : undefined}
      >
        <ModalBody>
          {mode === 'invoice' ? (
            <p className="text-sm text-foreground">
              Issues a one-off invoice for {selected ? formatZAR(selected.amount_due) : ''} under Terms 6.4(c) and
              emails it to the customer. The amount is the registration fee as paid — not pro-rated.
            </p>
          ) : (
            <FormField label="Why is no amount payable? (Terms 6.4(d))" required>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                placeholder="e.g. Hosting was terminated by a SightHost fault on 2026-08-05; the customer did not leave."
              />
            </FormField>
          )}
          {submitError && (
            <NotificationCard tone="danger" title="Could not record the decision" body={submitError} />
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setSelected(null)} disabled={busy}>Cancel</Button>
          <Button variant={mode === 'invoice' ? 'primary' : 'destructive'} onClick={submit} loading={busy} disabled={busy}>
            {mode === 'invoice' ? 'Issue invoice' : 'Record waiver'}
          </Button>
        </ModalFooter>
      </Modal>
    </UniversalPage>
  );
}
